import {
  Avatar,
  Button,
  Checkbox,
  Divider,
  Drawer,
  Form,
  Input,
  List,
  Radio,
  Select,
  Spin,
  notification,
  Modal,
  DatePicker,
  Popconfirm,
} from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DEFAULT_USER_PROFILE_IMAGE } from "../../../../../Constants/Api/azure/azure";
import { DeleteOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  buyACourse,
  getAllUsers,
  removeAUserFromACourse,
  issueCertificate,
  getCertificateByStudent,
  getCourseById,
  getStudentCourseGrade,
} from "../../../../../Constants/Api/functions/function";
import { setAllUsers } from "../../../../../Reducers/allUsersReducer";
import dayjs from "dayjs";

const { Option } = Select;

const CourseUsers = ({
  openCourseUsers,
  setOpenCourseUsers,
  getCourseUsers,
  courseId,
  courseUsers,
  course,
}) => {
  const [api, contextHolder] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const allUsers = useSelector((state) => state.allUsers.allUsers);
  const [newUsers, setNewUsers] = useState([]);
  const userProfile = useSelector((state) => state.userProfile.userProfile);
  const dispatch = useDispatch();
  const [isAddToPayments, setIsAddToPayments] = useState(false);
  const fromRef = useRef();
  const [certificateModalOpen, setCertificateModalOpen] = useState(false);
  const [selectedUserForCertificate, setSelectedUserForCertificate] =
    useState(null);
  const [certificateExists, setCertificateExists] = useState(false);
  const [certificateUrl, setCertificateUrl] = useState("");
  const [certificateNumber, setCertificateNumber] = useState("");
  const [autoGrade, setAutoGrade] = useState("");
  const [isCalculatingGrade, setIsCalculatingGrade] = useState(false);
  const certificateFormRef = useRef();
  const [userCertificateStatus, setUserCertificateStatus] = useState({});
  const [courseInstallments, setCourseInstallments] = useState([]);
  const [paymentPlanType, setPaymentPlanType] = useState('full');
  const enrolledUsers = useMemo(() => {
    if (!courseUsers) {
      return [];
    }

    return courseUsers.map((user) => ({
      id: user.id,
      indexNumber: user.studentNumber || user.indexNumber,
      studentNumber: user.studentNumber,
      name: user.name,
    }));
  }, [courseUsers]);

  const selectableUsers = useMemo(() => {
    if (!isAddToPayments) {
      return newUsers;
    }

    const mergedUsersMap = new Map();

    users?.forEach((user) => {
      mergedUsersMap.set(user.id, user);
    });

    enrolledUsers.forEach((user) => {
      if (mergedUsersMap.has(user.id)) {
        const existingUser = mergedUsersMap.get(user.id);
        mergedUsersMap.set(user.id, {
          ...existingUser,
          indexNumber: existingUser?.studentNumber || existingUser?.indexNumber || user.studentNumber || user.indexNumber,
          studentNumber: existingUser?.studentNumber || user.studentNumber,
          name: existingUser?.name || user.name,
          __isEnrolled: true,
        });
      } else {
        mergedUsersMap.set(user.id, {
          ...user,
          __isEnrolled: true,
        });
      }
    });

    return Array.from(mergedUsersMap.values());
  }, [isAddToPayments, newUsers, users, enrolledUsers]);
  const installmentOptions = useMemo(() => {
    if (courseInstallments?.length > 0) {
      return courseInstallments;
    }

    if (course?.installments) {
      const pricePerInstallment =
        course?.price && course.installments
          ? Number(course.price) / Number(course.installments)
          : 0;

      return Array.from({ length: course.installments }, (_, index) => ({
        installmentNumber: index + 1,
        amount: pricePerInstallment,
      }));
    }

    return [];
  }, [courseInstallments, course]);

  const activePromo = useMemo(() => {
    if (!course?.isPromoActive || !course?.promoPrice) return false;
    if (course.promoStartDate && course.promoEndDate) {
      const today = new Date();
      const start = new Date(course.promoStartDate);
      const end = new Date(course.promoEndDate);
      end.setHours(23, 59, 59, 999);
      return today >= start && today <= end;
    }
    return false;
  }, [course]);

  const fullPrice = activePromo ? course?.promoPrice : course?.price;
  const planName = activePromo ? "Plan 1 - Promotional Full Payment" : "Plan 2 - Standard Full Payment";

  const handleGetAllUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      if (userProfile?.role === "superadmin" || userProfile?.role === "admin" || userProfile?.role === "coordinator" || userProfile?.role === "instructor") {
        const centerId = userProfile?.role === "superadmin" ? null : 2;
        const response = await getAllUsers(centerId);
        if (response) {
          dispatch(setAllUsers(response));
          setUsers(response);
        }
      }
    } catch (error) {
      console.error("getAllUsers", error);
    }
    setIsLoading(false);
  }, [dispatch, userProfile]);

  useEffect(() => {
    if (allUsers.length < 1) {
      handleGetAllUsers();
    } else {
      setUsers(allUsers);
    }
  }, [allUsers, handleGetAllUsers]);

  useEffect(() => {
    if (course && fromRef.current) {
      fromRef.current.setFieldsValue({
        paymentAmount: course.price?.toString(),
      });
    }
  }, [course]);

  useEffect(() => {
    if (users?.length > 0 && courseUsers?.length > 0) {
      const newUsers = users.filter(
        (user) => !courseUsers.find((courseUser) => courseUser.id === user.id)
      );

      setNewUsers(newUsers);
    } else {
      setNewUsers(users);
    }
  }, [users, courseUsers]);

  // Fetch course installments when drawer opens
  useEffect(() => {
    const fetchCourseInstallments = async () => {
      if (openCourseUsers && courseId) {
        try {
          const courseData = await getCourseById(courseId);
          const normalizedCourse = Array.isArray(courseData) ? courseData[0] : courseData;

          if (normalizedCourse?.installmentBreakdown) {
            setCourseInstallments(normalizedCourse.installmentBreakdown);
          } else {
            setCourseInstallments([]);
          }
        } catch (error) {
          console.error("Error fetching course installments:", error);
          setCourseInstallments([]);
        }
      }
    };

    fetchCourseInstallments();
  }, [openCourseUsers, courseId]);

  // Check certificate status for all course users
  useEffect(() => {
    const checkAllCertificates = async () => {
      if (courseUsers && courseUsers.length > 0 && openCourseUsers) {
        try {
          // Check all certificates in parallel
          const certificatePromises = courseUsers.map((user) =>
            getCertificateByStudent(user?.id, courseId)
              .then((cert) => ({ userId: user?.id, hasCertificate: !!cert }))
              .catch(() => ({ userId: user?.id, hasCertificate: false }))
          );

          const results = await Promise.all(certificatePromises);

          const certificateStatusMap = {};
          results.forEach((result) => {
            if (result.hasCertificate) {
              certificateStatusMap[result.userId] = true;
            }
          });

          setUserCertificateStatus(certificateStatusMap);
        } catch (error) {
          console.error("Error checking certificates:", error);
        }
      }
    };

    checkAllCertificates();
  }, [courseUsers, openCourseUsers, courseId]);

  const onFinish = async (values) => {
    setIsLoading(true);
    values.courseId = courseId;

    if (values.isAddToPayments) {
      if (paymentPlanType === 'installments') {
        const selectedInstallments = Array.isArray(values.payedInstallments)
          ? values.payedInstallments.map(Number)
          : [];

        const installmentsForSubmission = selectedInstallments
          .map((installmentNumber) =>
            installmentOptions.find(
              (installment) => installment.installmentNumber === installmentNumber
            )
          )
          .filter(Boolean)
          .map((installment) => ({
            installmentNumber: installment.installmentNumber,
            amount: Number(installment.amount) || 0,
          }));

        const calculatedAmount = installmentsForSubmission.reduce(
          (sum, installment) => sum + installment.amount,
          0
        );

        values.amount = calculatedAmount;
        values.paymentAmount = calculatedAmount.toFixed(2);
        values.installmentPayments = installmentsForSubmission;
        values.payedInstallments = selectedInstallments.length > 0 ? Math.max(...selectedInstallments) : 0;
        values.isPaid = selectedInstallments.length > 0 && selectedInstallments.length === installmentOptions.length;
      } else {
        // Full Payment
        values.amount = Number(fullPrice) || 0;
        values.paymentAmount = (Number(fullPrice) || 0).toFixed(2);
        values.installmentPayments = [];
        values.payedInstallments = 1;
        values.isPaid = true;
      }
    } else {
      values.isPaid = false;
      values.isAddToPayments = false;
      values.payedInstallments = 0;
      values.installmentPayments = [];
    }

    try {
      const response = await buyACourse(values);
      if (response) {
        getCourseUsers();
        api.success({
          message: "User added to course",
        });
      } else {
        api.error({
          message: "Failed to add user to course",
        });
      }
    } catch (error) {
      console.error("onFinish", error);
      api.error({
        message: "Failed to add user to course",
      });
    }
    setIsLoading(false);
  };

  const handleDeleteUser = async (userId) => {
    setIsLoading(true);
    try {
      const response = await removeAUserFromACourse(userId, courseId);
      if (response) {
        getCourseUsers();
        api.success({
          message: "User removed from course",
        });
      }
    } catch (error) {
      console.error("handleDeleteUser", error);
      api.error({
        message: "Failed to remove user from course",
      });
    }
    setIsLoading(false);
  };

  const handleOpenCertificateModal = async (user) => {
    setSelectedUserForCertificate(user);
    setCertificateModalOpen(true);
    setAutoGrade("");
    setIsCalculatingGrade(true);
    certificateFormRef.current?.resetFields();

    // Check if certificate already exists
    try {
      const cert = await getCertificateByStudent(user?.id, courseId);
      if (cert) {
        setCertificateExists(true);
        setCertificateUrl(cert?.CertificateURL);
        setUserCertificateStatus((prev) => ({ ...prev, [user?.id]: true }));
      } else {
        setCertificateExists(false);
        setCertificateUrl("");
      }
    } catch (error) {
      setCertificateExists(false);
      setCertificateUrl("");
    }

    // Auto-calculate grade
    try {
      const gradeRes = await getStudentCourseGrade(user?.id, courseId);
      if (gradeRes && gradeRes.grade) {
        setAutoGrade(gradeRes.grade);
        certificateFormRef.current?.setFieldsValue({
          finalGrade: gradeRes.grade,
        });
      } else {
        setAutoGrade("Pass");
        certificateFormRef.current?.setFieldsValue({
          finalGrade: "Pass",
        });
      }
    } catch (error) {
      console.error("Error auto-fetching student grade:", error);
      setAutoGrade("Pass");
      certificateFormRef.current?.setFieldsValue({
        finalGrade: "Pass",
      });
    } finally {
      setIsCalculatingGrade(false);
    }

    // Generate certificate number
    try {
      let courseDataFetched = await getCourseById(courseId);

      if (Array.isArray(courseDataFetched)) {
        courseDataFetched = courseDataFetched[0];
      }

      const courseCode = courseDataFetched?.courseid || "";
      const studentFirstLetter = user?.name
        ? user.name.charAt(0).toUpperCase()
        : "";
      const studentIndexNumber = user?.studentNumber || user?.indexNumber || "";

      const generatedNumber = user?.studentNumber 
        ? `${courseCode}-${user.studentNumber}`
        : `${courseCode}-${studentFirstLetter}${studentIndexNumber}`;
      setCertificateNumber(generatedNumber);
    } catch (error) {
      console.error("Error generating certificate number:", error);
    }
  };

  const handleIssueCertificate = async (values) => {
    if (!courseId || !selectedUserForCertificate?.id) {
      api.error({
        message: "Error",
        description: "Course ID or User ID not found",
      });
      return;
    }

    // Transform camelCase to PascalCase for backend API
    const certificateData = {
      StudentID: selectedUserForCertificate?.id,
      CourseID: courseId,
      CertificateURL: values.certificateUrl || certificateUrl,
      CertificateNumber: certificateNumber || values.certificateNumber,
      Grade: values.finalGrade || autoGrade || null,
      IssueDate: values.issueDate
        ? values.issueDate.format("YYYY-MM-DD")
        : new Date().toISOString().split("T")[0],
    };

    setIsLoading(true);

    try {
      const res = await issueCertificate(certificateData);
      if (res && res.status === 200) {
        api.success({
          message: "Success",
          description: "Certificate issued successfully",
        });
        setCertificateExists(true);
        setCertificateUrl(certificateData.CertificateURL);
        setUserCertificateStatus((prev) => ({
          ...prev,
          [selectedUserForCertificate?.id]: true,
        }));
        setCertificateModalOpen(false);
        certificateFormRef.current?.resetFields();
      } else {
        api.error({
          message: "Error",
          description: res?.data?.message || "Failed to issue certificate",
        });
      }
    } catch (error) {
      console.error("IssueCertificate", error);
      api.error({
        message: "Error",
        description: "Something went wrong",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportCourseUsers = () => {
    if (!courseUsers || courseUsers.length === 0) return;

    const headers = ["Index Number", "Name", "Email", "Course"];
    const rows = courseUsers.map((user) => [
      user.indexNumber || "",
      user.name || "",
      user.email || "",
      course?.name || "",
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8,\uFEFF" +
      [headers.join(","), ...rows.map((r) => r.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    const fileName = `course_users_${String(course?.name || 'course').toLowerCase().replace(/\s+/g, '_')}.csv`;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Drawer
        title="Course Users"
        width={720}
        onClose={() => setOpenCourseUsers(false)}
        open={openCourseUsers}
        extra={
          courseUsers?.length > 0 && (
            <Button type="default" onClick={handleExportCourseUsers}>
              Export User List
            </Button>
          )
        }
        styles={{
          body: {
            paddingBottom: 80,
          },
        }}
      >
        {contextHolder}
        <Spin spinning={isLoading} tip="Please wait...">
          {userProfile?.role !== "instructor" && (
            <Form layout="vertical" onFinish={onFinish} ref={fromRef}>
              <Form.Item
                name="userId"
                label="User"
                rules={[
                  {
                    required: true,
                    message: "Please select user",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select user"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                    0
                  }
                >
                  {selectableUsers.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {`${user?.indexNumber || "N/A"} - ${user.name}${user?.__isEnrolled ? " (Enrolled)" : ""
                        }`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="isAddToPayments"
                label="Add to payments"
                rules={[
                  {
                    required: true,
                    message: "Please select add to payments",
                  },
                ]}
              >
                <Radio.Group
                  onChange={(e) => {
                    setIsAddToPayments(e.target.value);
                    fromRef.current?.setFieldsValue({ userId: undefined });
                  }}
                >
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>

              {isAddToPayments && (
                <>
                  <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 8, border: '1px solid #d9d9d9' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Available Payment Plans for this Course:</div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 200, background: '#fff', padding: 12, borderRadius: 6, border: '1px solid #e8e8e8' }}>
                            <strong style={{ color: activePromo ? '#d32f2f' : '#1890ff' }}>{planName}</strong>
                            <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 4 }}>
                                {activePromo && <span style={{ textDecoration: 'line-through', color: '#999', fontSize: 12, marginRight: 6 }}>{course?.price} LKR</span>}
                                {fullPrice} LKR
                            </div>
                        </div>
                        {installmentOptions.length > 0 && (
                            <div style={{ flex: 1, minWidth: 200, background: '#fff', padding: 12, borderRadius: 6, border: '1px solid #e8e8e8' }}>
                                <strong style={{ color: '#52c41a' }}>Plan 3 - Installment Plan</strong>
                                <div style={{ fontSize: 13, marginTop: 4 }}>
                                    {installmentOptions.length} Payments (See breakdown below)
                                </div>
                            </div>
                        )}
                    </div>
                  </div>

                  <Form.Item
                    label="Payment Plan Selection"
                    rules={[{ required: true, message: "Please select a payment plan" }]}
                  >
                    <Radio.Group
                      value={paymentPlanType}
                      onChange={(e) => {
                        setPaymentPlanType(e.target.value);
                        if (e.target.value === 'full') {
                          fromRef.current?.setFieldsValue({
                            paymentAmount: Number(fullPrice).toFixed(2)
                          });
                        } else {
                          fromRef.current?.setFieldsValue({
                            payedInstallments: [],
                            paymentAmount: "0.00"
                          });
                        }
                      }}
                    >
                      <Radio value="full">Full Payment ({activePromo ? 'Plan 1' : 'Plan 2'})</Radio>
                      {installmentOptions.length > 0 && (
                        <Radio value="installments">Installment Plan (Plan 3)</Radio>
                      )}
                    </Radio.Group>
                  </Form.Item>

                  <Form.Item
                    name="paymentStatus"
                    label="Payment Status"
                    rules={[
                      {
                        required: true,
                        message: "Please select payment status",
                      },
                    ]}
                  >
                    <Radio.Group>
                      <Radio value="pending">Pending</Radio>
                      <Radio value="paid">Completed</Radio>
                    </Radio.Group>
                  </Form.Item>

                  {paymentPlanType === 'installments' && (
                    <Form.Item
                      name="payedInstallments"
                      label="Paid Installments"
                      rules={[
                        {
                          required: true,
                          type: "array",
                          min: 1,
                          message: "Please select at least one installment",
                        },
                      ]}
                    >
                      <Checkbox.Group
                        onChange={(selectedValues = []) => {
                          if (!fromRef.current) {
                            return;
                          }

                          const numericSelections = selectedValues.map((value) =>
                            Number(value)
                          );

                          const amountMap = installmentOptions.reduce(
                            (acc, installment) => {
                              acc.set(
                                installment.installmentNumber,
                                parseFloat(installment.amount) || 0
                              );
                              return acc;
                            },
                            new Map()
                          );

                          const totalAmount = numericSelections.reduce(
                            (sum, value) => sum + (amountMap.get(value) || 0),
                            0
                          );

                          fromRef.current.setFieldsValue({
                            payedInstallments: numericSelections,
                            paymentAmount: totalAmount.toFixed(2),
                          });
                        }}
                        style={{ width: "100%" }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                          }}
                        >
                          {installmentOptions.map((installment) => (
                            <Checkbox
                              key={installment.installmentNumber}
                              value={installment.installmentNumber}
                            >
                              {`Installment ${installment.installmentNumber} - Amount: LKR ${(
                                parseFloat(installment.amount) || 0
                              ).toFixed(2)}`}
                            </Checkbox>
                          ))}
                        </div>
                      </Checkbox.Group>
                    </Form.Item>
                  )}

                  <Form.Item
                    name="paymentAmount"
                    label="Payment Amount"
                    rules={[
                      {
                        required: true,
                        message: "Please enter payment amount",
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      placeholder="Calculated payment amount"
                      readOnly
                    />
                  </Form.Item>

                  <Form.Item
                    name="paymentMethod"
                    label="Payment Method"
                    rules={[
                      {
                        required: true,
                        message: "Please select payment method",
                      },
                    ]}
                  >
                    <Select placeholder="Select payment method">
                      <Option value="cash">Cash</Option>
                      <Option value="card">Card</Option>
                      <Option value="bank">Bank</Option>
                    </Select>
                  </Form.Item>
                </>
              )}

              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Add User
                </Button>
              </Form.Item>
            </Form>
          )}

          <Divider />

          <List
            itemLayout="horizontal"
            dataSource={courseUsers || []}
            renderItem={(item) => (
              <List.Item
                extra={
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "center",
                    }}
                  >
                    {userCertificateStatus[item?.id] ? (
                      <span
                        style={{
                          color: "green",
                          fontWeight: "bold",
                          fontSize: "12px",
                          padding: "4px 12px",
                          backgroundColor: "#f6ffed",
                          border: "1px solid #b7eb8f",
                          borderRadius: "4px",
                        }}
                      >
                        ✓ Issued
                      </span>
                    ) : (
                      <Button
                        type="primary"
                        onClick={() => handleOpenCertificateModal(item)}
                        size="small"
                      >
                        Issue Certificate
                      </Button>
                    )}
                    {userProfile?.role !== "instructor" && (
                      <Popconfirm
                        title="Are you sure you want to remove this user from the course?"
                        onConfirm={() => handleDeleteUser(item.id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button
                          type="link"
                          danger
                          size="small"
                        >
                          <DeleteOutlined />
                        </Button>
                      </Popconfirm>
                    )}
                  </div>
                }
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      src={item.profileImage || DEFAULT_USER_PROFILE_IMAGE}
                    />
                  }
                  title={
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '16px', fontSize: '13px', color: '#595959', whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: '600', color: '#000', fontSize: '14px' }}>{item.name}</span>
                      {item.indexNumber && (
                        <span>
                          <strong>;</strong> {item.indexNumber}
                        </span>
                      )}
                    </div>
                  }
                  description={null}
                />
              </List.Item>
            )}
          />
        </Spin>
      </Drawer>

      <Modal
        title="Issue Certificate"
        open={certificateModalOpen}
        onCancel={() => setCertificateModalOpen(false)}
        footer={null}
        width={600}
      >
        <Spin spinning={isLoading} tip="Please wait...">
          {certificateExists ? (
            <div
              style={{
                marginBottom: "20px",
                padding: "15px",
                backgroundColor: "#f6ffed",
                border: "1px solid #b7eb8f",
                borderRadius: "4px",
              }}
            >
              <p
                style={{
                  color: "green",
                  fontWeight: "bold",
                  marginBottom: "10px",
                }}
              >
                ✓ Certificate Already Issued
              </p>
              <p>
                <strong>Certificate URL:</strong>{" "}
                <a href={certificateUrl} target="_blank" rel="noreferrer">
                  {certificateUrl}
                </a>
              </p>
            </div>
          ) : (
            <Form
              layout="vertical"
              onFinish={handleIssueCertificate}
              ref={certificateFormRef}
            >
              <Form.Item label="Student ID (Auto-filled)" name="studentId">
                <Input
                  disabled
                  value={selectedUserForCertificate?.id}
                  placeholder={selectedUserForCertificate?.id}
                />
              </Form.Item>

              <Form.Item
                label="Certificate URL"
                name="certificateUrl"
                rules={[
                  { required: true, message: "Please enter certificate URL!" },
                ]}
              >
                <Input placeholder="https://example.com/certificate" />
              </Form.Item>

              <Form.Item
                label="Issue Date"
                name="issueDate"
                initialValue={dayjs()}
                rules={[
                  { required: true, message: "Please select issue date!" },
                ]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                label="Certificate Number (Auto-filled)"
                name="certificateNumber"
              >
                <Input
                  disabled
                  value={certificateNumber}
                  placeholder={certificateNumber}
                />
              </Form.Item>

              <Form.Item
                label={
                  <span>
                    Final Grade (Auto Calculated)
                    {isCalculatingGrade && <Spin size="small" style={{ marginLeft: 8 }} />}
                    {autoGrade && (
                      <span style={{ fontSize: '12px', color: '#52c41a', marginLeft: 8, fontWeight: 'normal' }}>
                        ✓ Auto-calculated: {autoGrade}
                      </span>
                    )}
                  </span>
                }
                name="finalGrade"
              >
                <Select
                  placeholder={isCalculatingGrade ? "Calculating grade..." : "Select or auto-filled grade"}
                  allowClear
                  loading={isCalculatingGrade}
                >
                  <Select.Option value="Distinction Pass">Distinction Pass</Select.Option>
                  <Select.Option value="Merit Pass">Merit Pass</Select.Option>
                  <Select.Option value="Credit Pass">Credit Pass</Select.Option>
                  <Select.Option value="Ordinary Pass">Ordinary Pass</Select.Option>
                  <Select.Option value="Pass">Pass</Select.Option>
                  <Select.Option value="Not Qualified">Not Qualified</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isLoading}
                  block
                >
                  Issue Certificate
                </Button>
              </Form.Item>
            </Form>
          )}
        </Spin>
      </Modal>
    </>
  );
};

export default CourseUsers;
